// Admin: per-merchant user CRUD page. Reachable via the merchant detail
// page's Users tab. Surfaces issue-reset (returns the URL once for OOB
// sharing) and clear-mfa.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  ApiError,
  adminMerchantUsers,
  type AdminMerchantUser,
  type AdminMerchantUserResetIssued,
  type MerchantRole,
} from '@/api/client';
import { ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ROLES: MerchantRole[] = ['owner', 'developer', 'finance', 'viewer'];

export const Route = createFileRoute('/_authed/merchants/$merchantId/users')({
  component: AdminMerchantUsersPage,
});

function AdminMerchantUsersPage() {
  const { merchantId } = Route.useParams();
  const { t } = useTranslation();
  const list = useQuery({
    queryKey: ['admin-merchant-users', merchantId],
    queryFn: () => adminMerchantUsers.list(merchantId),
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminMerchantUser | null>(null);
  const [resetView, setResetView] = useState<AdminMerchantUserResetIssued | null>(null);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('admin.merchant_users.title', { defaultValue: 'Merchant users' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('admin.merchant_users.subtitle', {
              defaultValue:
                'Create users, change roles, issue password resets, or clear MFA when an owner loses their authenticator.',
            })}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          {t('admin.merchant_users.create', { defaultValue: 'New user' })}
        </Button>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.col_username', { defaultValue: 'Username' })}</TableHead>
              <TableHead>{t('merchant.users.col_full_name', { defaultValue: 'Name' })}</TableHead>
              <TableHead>{t('users.col_role', { defaultValue: 'Role' })}</TableHead>
              <TableHead>{t('users.col_status', { defaultValue: 'Status' })}</TableHead>
              <TableHead>{t('users.col_mfa', { defaultValue: 'MFA' })}</TableHead>
              <TableHead className="text-end">
                {t('users.col_actions', { defaultValue: 'Actions' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.full_name}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={u.status === 'active' ? 'secondary' : 'outline'}
                    className="text-[10px] uppercase"
                  >
                    {u.status}
                  </Badge>
                </TableCell>
                <TableCell>{u.mfa_enabled ? '✓' : '—'}</TableCell>
                <TableCell className="text-end">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(u)}>
                    {t('users.edit', { defaultValue: 'Edit' })}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {creating && (
        <CreateDialog
          merchantId={merchantId}
          onClose={() => setCreating(false)}
          onIssued={(r) => setResetView(r)}
        />
      )}
      {editing && (
        <EditDialog
          merchantId={merchantId}
          user={editing}
          onClose={() => setEditing(null)}
          onIssued={(r) => setResetView(r)}
        />
      )}
      {resetView && (
        <ResetIssuedDialog issued={resetView} onClose={() => setResetView(null)} />
      )}
    </div>
  );
}

function CreateDialog({
  merchantId,
  onClose,
  onIssued,
}: {
  merchantId: string;
  onClose: () => void;
  onIssued: (r: AdminMerchantUserResetIssued) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState<MerchantRole>('owner');

  const create = useMutation({
    mutationFn: () =>
      adminMerchantUsers.create(merchantId, {
        username,
        full_name: fullName,
        email: email || undefined,
        mobile: mobile || undefined,
        role,
      }),
    onSuccess: (out) => {
      qc.invalidateQueries({ queryKey: ['admin-merchant-users', merchantId] });
      onClose();
      if (out.reset) onIssued(out.reset);
      else toast.success(t('admin.merchant_users.created', { defaultValue: 'User created.' }));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Create failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('admin.merchant_users.create', { defaultValue: 'New user' })}
            </DialogTitle>
            <DialogDescription>
              {t('admin.merchant_users.create_hint', {
                defaultValue:
                  'No password? Server generates one and returns a one-time reset URL — share it with the user.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t('users.col_username', { defaultValue: 'Username' })}</Label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.full_name', { defaultValue: 'Full name' })}</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.email', { defaultValue: 'Email' })}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.mobile', { defaultValue: 'Mobile (E.164)' })}</Label>
            <Input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as MerchantRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('common.saving', { defaultValue: 'Saving…' })
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  merchantId,
  user,
  onClose,
  onIssued,
}: {
  merchantId: string;
  user: AdminMerchantUser;
  onClose: () => void;
  onIssued: (r: AdminMerchantUserResetIssued) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [role, setRole] = useState<MerchantRole>(user.role);
  const [status, setStatus] = useState<'active' | 'disabled'>(user.status);

  const update = useMutation({
    mutationFn: () => adminMerchantUsers.update(merchantId, user.id, { role, status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-merchant-users', merchantId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const reissue = useMutation({
    mutationFn: () => adminMerchantUsers.issueReset(merchantId, user.id),
    onSuccess: (r) => onIssued(r),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Issue failed'),
  });

  const disable = useMutation({
    mutationFn: () => adminMerchantUsers.disable(merchantId, user.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-merchant-users', merchantId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Disable failed'),
  });

  const clearMfa = useMutation({
    mutationFn: () => adminMerchantUsers.clearMfa(merchantId, user.id),
    onSuccess: () => {
      toast.success(t('admin.merchant_users.mfa_cleared', { defaultValue: 'MFA cleared.' }));
      qc.invalidateQueries({ queryKey: ['admin-merchant-users', merchantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Clear failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('users.edit_title', {
                defaultValue: 'Edit {{name}}',
                name: user.username,
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as MerchantRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('users.col_status', { defaultValue: 'Status' })}</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as 'active' | 'disabled')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => reissue.mutate()}
                disabled={reissue.isPending}
              >
                {t('admin.merchant_users.issue_reset', { defaultValue: 'Issue reset link' })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => clearMfa.mutate()}
                disabled={!user.mfa_enabled || clearMfa.isPending}
              >
                {t('admin.merchant_users.clear_mfa', { defaultValue: 'Clear MFA' })}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => disable.mutate()}
                disabled={user.status === 'disabled' || disable.isPending}
              >
                {t('users.disable', { defaultValue: 'Disable' })}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetIssuedDialog({
  issued,
  onClose,
}: {
  issued: AdminMerchantUserResetIssued;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('admin.merchant_users.reset_title', {
              defaultValue: 'One-time reset link',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('admin.merchant_users.reset_hint', {
              defaultValue:
                'Share this URL with the user via WhatsApp or phone. The link is single-use and expires.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{t('admin.merchant_users.reset_url', { defaultValue: 'Reset URL' })}</Label>
            <Input value={issued.url} readOnly onFocus={(e) => e.currentTarget.select()} />
          </div>
          <div className="space-y-1">
            <Label>{t('admin.merchant_users.reset_token', { defaultValue: 'Token' })}</Label>
            <Input value={issued.token} readOnly onFocus={(e) => e.currentTarget.select()} />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin.merchant_users.reset_expires', {
              defaultValue: 'Expires {{when}}',
              when: new Date(issued.expires_at).toLocaleString(),
            })}
          </p>
          <Button
            className="w-full"
            onClick={() => navigator.clipboard.writeText(issued.url).then(() => onClose())}
          >
            {t('admin.merchant_users.copy_close', { defaultValue: 'Copy URL & close' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
